import { Box, Button, Divider, Group, IconFont } from '@byecode/ui'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { Field, TypeInstanceMap } from '@lighthouse/core'
import { DATE_SYSTEM, generateJson, getDateVariableOption, INNER_TYPE_CANNOT_DISPLAY_AS_TEXT, useAtomData } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { useCallback, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { lastPageOfStackAtom, pageVariableSchemasFamily } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM } from '../constants'
import { SortableConfigure } from './SortableConfigure'

const filterField = (arg: Field | TypeInstanceMap) => {
    const innerType = typeof arg === 'object' ? arg.innerType : arg
    return !INNER_TYPE_CANNOT_DISPLAY_AS_TEXT.has(innerType)
}
export const CollapseSetting = () => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const { control } = useFormContext()
    const styleProps = getBlockAndPageDesignLimit('collapse')
    const {
        fields: rawFields,
        remove,
        move,
        append
    } = useFieldArray({
        control,
        name: 'list',
        keyName: 'key'
    })
    const fields = rawFields as Array<{ key: string; id: string }>

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 8 }
        }),
        useSensor(TouchSensor, {
            activationConstraint: { distance: 8 }
        })
    )

    const [activeId, setActiveId] = useState<string | null>(null)

    const onDragStart = (e: DragStartEvent) => {
        if (e.active) {
            setActiveId(e.active.id as string)
        }
    }

    const onDragEnd = ({ over }: DragEndEvent) => {
        if (over && activeId) {
            const overIndex = fields.findIndex(item => item.id === over.id)
            const activeIndex = fields.findIndex(item => item.id === activeId)
            if (activeIndex !== overIndex) {
                move(activeIndex, overIndex)
            }
        }
    }

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const dateOption = useMemo(() => getDateVariableOption(DATE_SYSTEM), [])
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const items = useMemo(() => fields.map(item => item.id), [fields])

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    return (
        <>
            <BlockConfigureSegmentedControl value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Controller control={control} name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="面板配置">
                        <Box px={8}>
                            <div style={{ overflowY: 'auto', margin: '8px 0' }}>
                                <DndContext
                                    sensors={sensors}
                                    modifiers={[restrictToVerticalAxis, restrictToWindowEdges, restrictToFirstScrollableAncestor]}
                                    onDragStart={onDragStart}
                                    onDragEnd={onDragEnd}
                                    onDragCancel={() => setActiveId(null)}
                                >
                                    <SortableContext disabled={disabledWithVersion} items={items} strategy={verticalListSortingStrategy}>
                                        {fields.map((item, index) => (
                                            <SortableConfigure
                                                key={item.id}
                                                disabled={disabledWithVersion}
                                                index={index}
                                                id={item.id}
                                                viewOptions={customViewOption ? [customViewOption] : undefined}
                                                pageOptions={pageOptions}
                                                userOption={userOption}
                                                dateOption={dateOption}
                                                pageVariableSchemas={pageVariableSchemas}
                                                datasourceList={dataSourceList}
                                                onRemove={remove}
                                            />
                                        ))}
                                    </SortableContext>
                                </DndContext>
                            </div>
                            <Button
                                style={{ gap: 6, padding: '0 12px' }}
                                radius={32}
                                onClick={() => {
                                    append({
                                        id: nanoid(),
                                        title: generateJson('标题'),
                                        subTitle: generateJson('副标题'),
                                        introduction: generateJson('请输入描述')
                                    })
                                }}
                                icon={<IconFont size={16} fill="var(--color-gray-500)" type="Add" />}
                            >
                                添加
                            </Button>
                        </Box>
                    </Group>
                    <Divider />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}
            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
